import api from '../utils/api';
import type { TeacherWeeklySchedule } from '../types/teacherWeeklySchedule';

export interface ListParams {
  weekStart?: string;
  weekEnd?: string;
  teacherId?: string;
  reference?: string;
}

export interface CreatePayload {
  teacherId: string;
  ngayGiang: string;
  buoiGiang: string;
  buoiSo: number;
  khoaHocId: string;
  monHocId: string;
  lopHocPhanId?: string;
  phongHocId: string;
  nhomThucHanhId?: string;
  ghiChu?: string;
  isLocked?: boolean;
}

export type UpdatePayload = Partial<Omit<CreatePayload, 'teacherId' | 'ngayGiang'>> & {
  ngayGiang?: string;
};

export interface ListResult {
  data: TeacherWeeklySchedule[];
  range?: { start: string; end: string };
}

const BASE_URL = '/api/v1/teacher-weekly-schedules';

const mapSchedule = (record: any): TeacherWeeklySchedule => ({
  id: Number(record?.Id ?? record?.id ?? 0),
  teacherId: String(record?.TeacherId ?? record?.teacherId ?? ''),
  ngayGiang: record?.NgayGiang ?? record?.ngayGiang ?? '',
  buoiGiang: record?.BuoiGiang ?? record?.buoiGiang ?? '',
  buoiSo: Number(record?.BuoiSo ?? record?.buoiSo ?? 0),
  khoaHocId: String(record?.KhoaHocId ?? record?.khoaHocId ?? ''),
  monHocId: String(record?.MonHocId ?? record?.monHocId ?? ''),
  lopHocPhanId: record?.LopHocPhanId != null
    ? String(record.LopHocPhanId)
    : record?.lopHocPhanId != null
      ? String(record.lopHocPhanId)
      : undefined,
  phongHocId: String(record?.PhongHocId ?? record?.phongHocId ?? ''),
  nhomThucHanhId: record?.NhomThucHanhId
    ? String(record.NhomThucHanhId)
    : record?.nhomThucHanhId
      ? String(record.nhomThucHanhId)
      : undefined,
  tenKhoaHoc: record?.TenKhoaHoc ?? record?.tenKhoaHoc ?? undefined,
  tenMonHoc: record?.TenMonHoc ?? record?.tenMonHoc ?? undefined,
  tenLopHP: record?.TenLopHP ?? record?.tenLopHP ?? record?.TenLHP ?? undefined,
  tenPhongHoc: record?.TenPhongHoc ?? record?.tenPhongHoc ?? undefined,
  tenNhomThucHanh: record?.TenNhomThucHanh ?? record?.tenNhomThucHanh ?? undefined,
  ghiChu: record?.GhiChu ?? record?.ghiChu ?? undefined,
  isLocked: Boolean(record?.IsLocked ?? record?.isLocked ?? false),
  createdAt: record?.CreatedAt ?? record?.createdAt ?? undefined,
  updatedAt: record?.UpdatedAt ?? record?.updatedAt ?? undefined,
});

const unwrapData = <T,>(payload: any, fallback?: T): T => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload.data ?? fallback) as T;
  }
  return (payload ?? fallback) as T;
};

export const teacherWeeklyScheduleService = {
  async list(params: ListParams = {}): Promise<ListResult> {
    const response = await api.get(BASE_URL, { params });
    const payload = response.data;
    const rowsSource = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
    const mapped = Array.isArray(rowsSource) ? rowsSource.map(mapSchedule) : [];

    return {
      data: mapped,
      range: payload?.range,
    };
  },

  async create(payload: CreatePayload): Promise<TeacherWeeklySchedule> {
    const response = await api.post(BASE_URL, payload);
    const data = unwrapData<any>(response.data);
    return mapSchedule(data);
  },

  async update(id: number, payload: UpdatePayload): Promise<TeacherWeeklySchedule> {
    const response = await api.put(`${BASE_URL}/${id}`, payload);
    const data = unwrapData<any>(response.data);
    return mapSchedule(data);
  },

  async remove(id: number): Promise<void> {
    await api.delete(`${BASE_URL}/${id}`);
  },

  async activate(id: number): Promise<any> {
    const response = await api.post(`${BASE_URL}/${id}/activate`);
    return unwrapData<any>(response.data);
  }
};

export type { TeacherWeeklySchedule };
